import { type ClassValue, clsx } from 'clsx'
import { kebabCase } from 'lodash'
import ms from 'ms'
import { twMerge } from 'tailwind-merge'
import tinycolor from 'tinycolor2'

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}
export const timeAgo = (timestamp: Date, timeOnly?: boolean): string => {
  if (!timestamp) return 'never'
  return `${ms(Date.now() - new Date(timestamp).getTime())}${timeOnly ? '' : ' ago'}`
}
export const toKebabCase = kebabCase
export async function fetcher<JSON = unknown>(input: RequestInfo, init?: RequestInit): Promise<JSON> {
  const res = await fetch(input, init)
  if (!res.ok) {
    const json = await res.json()
    if (json.error) {
      const error = new Error(json.error) as Error & {
        status: number
      }
      error.status = res.status
      throw error
    } else {
      throw new Error('An unexpected error occurred')
    }
  }

  return res.json()
}

export function nFormatter(num: number, digits?: number) {
  if (!num) return '0'
  const lookup = [
    { value: 1, symbol: '' },
    { value: 1e3, symbol: 'K' },
    { value: 1e6, symbol: 'M' },
    { value: 1e9, symbol: 'G' },
    { value: 1e12, symbol: 'T' },
    { value: 1e15, symbol: 'P' },
    { value: 1e18, symbol: 'E' },
  ]
  const rx = /\.0+$|(\.[0-9]*[1-9])0+$/
  const item = lookup
    .slice()
    .reverse()
    .find(function (item) {
      return num >= item.value
    })
  return item ? (num / item.value).toFixed(digits || 1).replace(rx, '$1') + item.symbol : '0'
}

export function capitalize(str: string) {
  if (!str || typeof str !== 'string') return str
  return str.charAt(0).toUpperCase() + str.slice(1)
}

export const truncate = (str: string, length: number) => {
  if (!str || str.length <= length) return str
  return `${str.slice(0, length)}...`
}

export const formatDate = (input: string | number) => {
  const date = new Date(input)
  return date.toLocaleString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: 'numeric',
  })
}
const currencyFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
})
export const cFormatter = (num: number) => {
  return currencyFormatter.format(num)
}

// Alias for consistency
export const formatCurrency = cFormatter

export const DefaultColorPalette = {
  canvas: tinycolor('#ffffff').toRgbString(),
  canvasAlt: tinycolor('#f4f5fa').toRgbString(),
  primary: tinycolor('#1f5c82').toRgbString(),
  secondary: tinycolor('#1E9FF2').toRgbString(),
  success: tinycolor('#28D094').toRgbString(),
  highlight: tinycolor('#f6e5c4').toRgbString(),
  info: tinycolor('#1EA026').toRgbString(),
  warning: tinycolor('#fcf8e3').toRgbString(),
  danger: tinycolor('#FF4961').toRgbString(),
  surfacePrimary: tinycolor('#343a40').toRgbString(),
  surfaceSecondary: tinycolor('#6c757d').toRgbString(),
  tertiary: tinycolor('#3c92dc').toRgbString(),
}
export type ColorPalette = Partial<typeof DefaultColorPalette>

export const isObject = (value: unknown): value is object =>
  value !== null && (typeof value === 'object' || typeof value === 'function')

export function isPromise<T = unknown>(value: unknown): value is Promise<T> {
  return (
    value instanceof Promise ||
    (isObject(value) &&
      'then' in value &&
      typeof value.then === 'function' &&
      'catch' in value &&
      typeof value.catch === 'function')
  )
}
